import { z } from 'zod';
import { API_ObjectAttributesSchema } from '../telematics/types-telematics';
import { AnalyticsCfgClusterSubMenuSchema, FilterSubMenuValueSchema } from '../telematics/types-analytics';
import { FilterColumnsSchema, FilterGroupsSchema } from '../telematics/types';
import { AvailableValuesSchema } from '../telematics/components/FilterByParameters/Filter';

export const LastMileConfigResponseSchema = z.object({
    availableTypes: z.array(API_ObjectAttributesSchema),
    availableValues: AvailableValuesSchema,
    subMenus: z.record(z.string(), FilterSubMenuValueSchema), // z.record(z.string(), z.any()),
    filterColumns: FilterColumnsSchema,
    filterGroups: FilterGroupsSchema,
    clusters: z.record(AnalyticsCfgClusterSubMenuSchema).or(z.array(z.any()).length(0)).optional(),
});
export type LastMileConfigResponseType = z.infer<typeof LastMileConfigResponseSchema>;

export const helpAndSupportSchema = z.object({
    email: z.string().catch(''),
    phone: z.string().catch(''),
});
export type HelpAndSupportType = z.infer<typeof helpAndSupportSchema>;

export const helpAndSupportDefaults: HelpAndSupportType = {
    email: 'pro@automapa.pl',
    phone: '+48 22 584 60 00',
};

export const routeTypes = ['shortest', 'fastest', 'balanced'];
export const RouteTypeSchema = z.union([...routeTypes.map(item => z.literal(item))]);
export type RouteTypeType = z.infer<typeof RouteTypeSchema>;

export const vehicleTypes = [
    'vehicle_category_1',
    'vehicle_category_1a',
    'vehicle_category_2',
    'vehicle_category_2a',
    'vehicle_category_3',
    'vehicle_category_4',
];
export const VehicleTypeSchema = z.union([...vehicleTypes.map(item => z.literal(item))]);
export type VehicleTypeType = z.infer<typeof VehicleTypeSchema>;

export const CalculationPeriodSchema = z.object({
    selected_method: z.string().describe('ex: days_of_week'),
    days_of_week: z.array(z.number()),
    days_of_month: z.array(z.number()),
});
export type CalculationPeriodType = z.infer<typeof CalculationPeriodSchema>;

// klasa emisji EURO5
export const vehicleEmissionClasses = ['euro_5'];
export const VehicleEmissionClassSchema = z.union([...vehicleEmissionClasses.map(item => z.literal(item))]);
export type VehicleEmissionClassType = z.infer<typeof VehicleEmissionClassSchema>;

export const vehicleMaxWeightTons = [40];
export const defaultCalculations = {
    route_type: 'shortest' as RouteTypeType, // DEPRECATED
    vehicle_type: 'vehicle_category_1' as VehicleTypeType, // DEPRECATED
    vehicle_emission_class: vehicleEmissionClasses[0], // DEPRECATED
    vehicle_max_weight_tons: vehicleMaxWeightTons[0], // DEPRECATED
    period: {
        selected_method: 'days_of_week',
        days_of_week: [],
        days_of_month: [],
    },
};

export const CustomerSettingsSchema = z.object({
    admin: z
        .object({
            show_data_reset_button: z.boolean().optional().catch(true),
            template_data: z.string().optional().catch(''),
        })
        .catch({}),
    expiration_date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/i)
        .nullable()
        .catch(null),
    calculations: z
        .object({
            route_type: RouteTypeSchema,
            vehicle_type: VehicleTypeSchema,
            vehicle_emission_class: VehicleEmissionClassSchema,
            vehicle_max_weight_tons: z.number(),
            period: CalculationPeriodSchema,
        })
        .catch(defaultCalculations),
    is_template: z.boolean().catch(false),
    map_key: z.string().catch(''),
    display: z
        .object({
            // icon_style: z.union([z.literal('classic'), z.literal('outline')]).catch('classic'),
            max_point_zoom: z.coerce.number().int().min(20).max(31).catch(26),
        })
        .catch({ max_point_zoom: 26 }),
    help_and_support: z
        .object({
            email: z.string().catch(helpAndSupportDefaults.email),
            phone: z.string().catch(helpAndSupportDefaults.phone),
        })
        .catch(helpAndSupportDefaults),
});
export type CustomerSettingsType = z.infer<typeof CustomerSettingsSchema>;

export const defaultClientSettings = {
    locale: 'pl',
    display: {
        showRelativeToday: true,
        icon_style: 'bold',
        max_point_zoom: 26,
        point_cluster_distance: 50,
    },
};

export const customerSettingsDefault = CustomerSettingsSchema.parse(defaultClientSettings);

export const ClientSettingsSchema = z
    .object({
        locale: z.string().catch(defaultClientSettings.locale),
        display: z
            .object({
                showRelativeToday: z.boolean().catch(defaultClientSettings.display.showRelativeToday),
                icon_style: z.string().default(defaultClientSettings.display.icon_style).catch(defaultClientSettings.display.icon_style),
                point_cluster_distance: z.number().catch(defaultClientSettings.display.point_cluster_distance),
                // max_point_zoom: z.coerce.number().int().min(20).max(31).catch(26)
            })
            .catch(defaultClientSettings.display),
    })
    .catch(defaultClientSettings);
export type ClientSettingsType = z.infer<typeof ClientSettingsSchema>;

export const clientSettingsDefault = ClientSettingsSchema.parse({});

export const DEFAULT_ALLOWED_AREA = 'pl'; // or 'eu'

export const defaultCustomerSettingsReadonly = {
    max_locations: 1000,
    max_connections: 5000,
    max_rows_per_file: 5000,
    max_transactions_per_month: null,
    allow_limit_exceeding: true,
    allow_file_import: true,
    ext_id_must_exists_in_full_name: true,
    short_name_instead_of_unique_id: true,
    allowed_area: DEFAULT_ALLOWED_AREA,
};

export const CustomerSettingsReadonlySchema = z
    .object({
        max_locations: z.number().nullable().catch(null),
        max_connections: z.number().nullable().catch(null),
        max_rows_per_file: z.number(),
        max_transactions_per_month: z.number().nullable().catch(null),
        allow_limit_exceeding: z.boolean().catch(true),
        allow_file_import: z.boolean(),
        ext_id_must_exists_in_full_name: z.boolean(),
        short_name_instead_of_unique_id: z.boolean(),
        allowed_area: z.string().nullable().catch(DEFAULT_ALLOWED_AREA),
    })
    .catch(defaultCustomerSettingsReadonly);
export type CustomerSettingsReadonlyType = z.infer<typeof CustomerSettingsReadonlySchema>;
